import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { PageHeader, StatusBadge, EmptyState } from "./shared/components";
import {
  getMyResignations,
  SeparationListItem,
  fetchAcknowledgmentPdf,
  generateWithdrawalOrder,
  normalizeSeparationKind,
  isActiveSeparation,
  hasActiveSeparation,
  hasBlockingSeparation,
  hasActiveHrInitiatedCase,
  getUserCapabilities,
  EsepCapabilities,
} from "./services/eSeparation.service";

// Each tab maps a friendly label → backend request_type "kind".
const FILTERS: { label: string; kind: string }[] = [
  { label: "All", kind: "ALL" },
  { label: "Intimation", kind: "RESIGNATION" },
  { label: "Short Notice", kind: "SHORT_NOTICE" },
  { label: "Withdrawal", kind: "WITHDRAWAL" },
  { label: "No Dues", kind: "NO_DUES" },
];

// Resolve a request row to its request_type kind (covers NO_DUES too, which the
// shared normalizeSeparationKind intentionally omits).
const rowKind = (r: SeparationListItem): string => {
  const up = String(r.raw?.request_type || r.type || "")
    .toUpperCase()
    .replace(/[\s-]/g, "_");
  if (up.includes("SHORT")) return "SHORT_NOTICE";
  if (up.includes("WITHDRAW")) return "WITHDRAWAL";
  if (up.includes("NO_DUES") || up.includes("NODUES")) return "NO_DUES";
  if (up.includes("RESIGN") || up.includes("INTIMATION")) return "RESIGNATION";
  return "";
};

const NEXT_ACTION_COLOR: Record<string, string> = {
  "Pending No-Dues": "#BA7517",
  "HOD Approval": "#185FA5",
  "HR Review": "#185FA5",
  "CA Approval": "#4A1A6E",
  "MD Approval": "#993C1D",
  Closed: "#78746D",
};

const nextActionColor = (action: string): string =>
  NEXT_ACTION_COLOR[action] || "#78746D";

const EseprationOverview: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const [requests, setRequests] = useState<SeparationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  // Server-side reasons a self-service request is unavailable (e.g. a short
  // notice once the No-Dues form is submitted).
  const [caps, setCaps] = useState<EsepCapabilities | null>(null);

  useEffect(() => {
    getUserCapabilities().then(setCaps);
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getMyResignations()
      .then((res) => {
        if (mounted) setRequests(res.data || []);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return requests.filter((r) => {
      const matchesFilter = filter === "ALL" || rowKind(r) === filter;
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        r.id.toLowerCase().includes(q) ||
        r.type.toLowerCase().includes(q);
      return matchesFilter && matchesSearch;
    });
  }, [requests, search, filter]);

  const activeCount = requests.filter(
    (r) => r.status !== "Closed" && r.status !== "Rejected"
  ).length;

  // Duplicate-prevention flags (mirrors the mobile app).
  // A resignation also blocks a new one once APPROVED (not just while active).
  const hasResignation = hasBlockingSeparation(requests, "RESIGNATION");
  // One short notice per resignation: it blocks while in process AND once approved
  // (an approved short notice has replaced the resignation); only a rejected one
  // may be re-raised.
  const hasShortNotice = hasBlockingSeparation(requests, "SHORT_NOTICE");
  const hasWithdrawal = hasActiveSeparation(requests, "WITHDRAWAL");

  // HR has a live termination / death case against the employee —
  // locks out every self-service separation request.
  const hrCaseActive = hasActiveHrInitiatedCase(requests);
  const HR_CASE_BLOCK_MSG =
    "An HR-initiated separation process (termination / death) is in progress for you. You cannot raise this request.";

  const resignationApproved = requests.some(
    (r) =>
      normalizeSeparationKind(r.type) === "RESIGNATION" &&
      String(r.status ?? "").toUpperCase() === "APPROVED"
  );

  const getDisabledInfo = (
    route: string
  ): { disabled: boolean; message: string } => {
    if (hrCaseActive) {
      return {
        disabled: true,
        message: caps?.employee.resignation_block_reason || HR_CASE_BLOCK_MSG,
      };
    }
    // Short notice and withdrawal act on an existing resignation — closed until the
    // employee has applied for one.
    if (
      (route === routes.Esepration.shortNotice || route === routes.Esepration.withdrawal) &&
      !hasResignation
    ) {
      return {
        disabled: true,
        message:
          "Apply for Intimation & Resignation first — this is only available once you have an existing resignation.",
      };
    }
    if (route === routes.Esepration.intimationResignation && hasResignation) {
      return {
        disabled: true,
        message: resignationApproved
          ? "Your resignation has already been approved. You cannot apply again."
          : "A resignation request is already in process. You can initiate a new one only after it is rejected.",
      };
    }
    if (
      route === routes.Esepration.shortNotice &&
      caps &&
      !caps.employee.can_submit_short_notice &&
      caps.employee.short_notice_block_reason
    ) {
      return { disabled: true, message: caps.employee.short_notice_block_reason };
    }
    if (route === routes.Esepration.shortNotice && hasShortNotice) {
      return {
        disabled: true,
        message:
          "A short notice request is already in process. You can initiate a new one only after it is rejected.",
      };
    }
    if (route === routes.Esepration.withdrawal && hasWithdrawal) {
      return {
        disabled: true,
        message:
          "A withdrawal request is already in process. You can initiate a new one only after it is rejected.",
      };
    }
    return { disabled: false, message: "" };
  };

  const showAlreadySubmitted = (message: string) => {
    toast.info(message);
  };

  // Short notice is raised against the active resignation; hand its id over so
  // the form can inherit every detail and only ask for the new relieving date.
  const activeResignation = useMemo(
    () =>
      requests.find(
        (r) =>
          normalizeSeparationKind(r.type) === "RESIGNATION" &&
          isActiveSeparation(r)
      ),
    [requests]
  );

  const openQuickAction = (route: string) => {
    if (route === routes.Esepration.shortNotice) {
      navigate(route, { state: { request_id: activeResignation?.id } });
      return;
    }
    navigate(route);
  };

  const openDetail = (r: SeparationListItem) => {
    const rt = String(
      r.raw?.request_type || r.raw?.separation_type || ""
    ).toUpperCase();
    const type = rt.includes("SHORT")
      ? "ShortNotice"
      : rt.includes("WITHDRAW")
      ? "Withdrawal"
      : "Intimation";
    // A No-Dues-only case has no resignation form / approval flow.
    if (r.display_status) {
      navigate(routes.Esepration.noDuesCaseDetail.replace(":id", r.id), {
        state: { request_id: r.id, role: "Employee" },
      });
      return;
    }
    navigate(routes.Esepration.approvalDetail.replace(":id", r.id), {
      state: { request_id: r.id, type, role: "Employee" },
    });
  };

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);

  const closePdf = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfTitle("");
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Withdrawal Order — available to the employee once their withdrawal is approved.
  const [orderLoadingId, setOrderLoadingId] = useState<string | null>(null);
  const handleDownloadOrder = async (r: SeparationListItem) => {
    setOrderLoadingId(r.id);
    const res = await generateWithdrawalOrder(r.id);
    setOrderLoadingId(null);
    if (res.success) toast.success("Withdrawal Order downloaded.");
    else toast.error(res.message || "Failed to download the Withdrawal Order.");
  };

  const handleDownload = async (r: SeparationListItem) => {
    setPdfLoadingId(r.id);
    const res = await fetchAcknowledgmentPdf(r.id);
    setPdfLoadingId(null);
    if (res.success && res.data) {
      setPdfTitle(`Acknowledgment · ${r.id}`);
      setPdfUrl(res.data);
    } else {
      toast.error(res.message || "Failed to load acknowledgment PDF.");
    }
  };

  // A termination / death case is handled entirely by HR — the employee has no
  // view of it (the server also returns nothing and refuses direct access).
  if (caps?.employee.access_locked) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <PageHeader title="E-Separation Overview" onExit={() => navigate(-1)} />
          <EmptyState
            icon="ti-lock"
            title="E-Separation is not available"
            message={
              caps.employee.access_locked_reason ||
              "This separation is handled by HR. E-Separation is not available to you."
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="E-Separation Overview" onExit={() => navigate(-1)} />

        {/* OVERVIEW */}
        <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: 12 }}>
          Overview
        </h6>
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <div className="text-muted small">Active Requests</div>
                <div className="fs-3 fw-bold" style={{ color: "#BA7517" }}>
                  {activeCount}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <div className="text-muted small">No-Dues Status</div>
                <div className="fs-3 fw-bold" style={{ color: "#185FA5" }}>
                  —
                </div>
                <small className="text-muted">Not yet initiated</small>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
              <div className="card-body d-flex align-items-center gap-3">
                <i className="ti ti-calendar-event fs-2" style={{ color: "#049123" }} />
                <div>
                  <div className="text-muted small">Expected Relieving</div>
                  <div className="fs-4 fw-bold" style={{ color: "#049123" }}>
                    —
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* QUICK ACTIONS */}
        <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: 12 }}>
          Quick Actions
        </h6>
        <div className="row g-3 mb-4">
          {[
            { label: "New Intimation", icon: "ti-file-plus", route: routes.Esepration.intimationResignation },
            { label: "Short Notice", icon: "ti-clock-bolt", route: routes.Esepration.shortNotice },
            { label: "Withdrawal", icon: "ti-arrow-back-up", route: routes.Esepration.withdrawal },
            // { label: "Bond Waiver", icon: "ti-receipt-2", route: routes.Esepration.bondWaiver },
          ].map((qa) => {
            const { disabled, message } = getDisabledInfo(qa.route);
            return (
              <div className="col-6 col-md-3" key={qa.label}>
                <div
                  className="card border-0 shadow-sm h-100 text-center"
                  style={{
                    borderRadius: 12,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.55 : 1,
                  }}
                  onClick={() =>
                    disabled
                      ? showAlreadySubmitted(message)
                      : openQuickAction(qa.route)
                  }
                >
                  <div className="card-body">
                    <i
                      className={`ti ${qa.icon} fs-3 d-block mb-2 ${
                        disabled ? "text-muted" : "text-primary"
                      }`}
                    />
                    <div
                      className="fw-medium small"
                      style={disabled ? { color: "#9a9a9a" } : undefined}
                    >
                      {qa.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* MY REQUESTS */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
          <div className="card-body">
            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: 12 }}>
              My Requests
            </h6>
            <div className="input-group mb-3">
              <span className="input-group-text bg-white">
                <i className="ti ti-search" />
              </span>
              <input
                className="form-control"
                placeholder="Search requests…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              {FILTERS.map((f) => (
                <button
                  key={f.kind}
                  className={`btn btn-sm ${filter === f.kind ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setFilter(f.kind)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="text-center py-5 text-muted">
                <div className="spinner-border text-primary mb-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="small">Loading your requests...</div>
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon="ti-file-off"
                title="No Requests Found"
                message={
                  requests.length === 0
                    ? "You have not submitted any separation requests yet."
                    : "No requests match your current filters."
                }
                action={
                  requests.length === 0 ? (
                    <button
                      className="btn btn-primary"
                      style={
                        hasResignation || hrCaseActive
                          ? { opacity: 0.55, cursor: "not-allowed" }
                          : undefined
                      }
                      onClick={() => {
                        const info = getDisabledInfo(
                          routes.Esepration.intimationResignation
                        );
                        if (info.disabled) {
                          showAlreadySubmitted(info.message);
                        } else {
                          navigate(routes.Esepration.intimationResignation);
                        }
                      }}
                    >
                      <i className="ti ti-plus me-1" />
                      New Intimation
                    </button>
                  ) : undefined
                }
              />
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr
                      className="text-muted text-uppercase"
                      style={{ fontSize: 11, letterSpacing: 0.4 }}
                    >
                      <th className="border-0">Request ID</th>
                      <th className="border-0">Type</th>
                      <th className="border-0">Status</th>
                      <th className="border-0">Next Action</th>
                      <th className="border-0">Submitted</th>
                      <th className="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id}>
                        <td className="fw-semibold">{r.id}</td>
                        <td className="text-muted">{r.type}</td>
                        <td>
                          <StatusBadge status={r.display_status || r.status} />
                        </td>
                        <td>
                          <span
                            className="small fw-medium"
                            style={{ color: nextActionColor(r.nextAction) }}
                          >
                            {r.nextAction}
                          </span>
                        </td>
                        <td className="text-muted small">{r.date}</td>
                        <td className="text-end">
                          <div className="d-inline-flex align-items-center gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openDetail(r)}
                              title="View details"
                            >
                              <i className="ti ti-eye" />
                            </button>
                            {normalizeSeparationKind(r.type) === "WITHDRAWAL" &&
                              String(r.status ?? "").toUpperCase() === "APPROVED" && (
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handleDownloadOrder(r)}
                                  disabled={orderLoadingId === r.id}
                                  title="Download Withdrawal Order"
                                >
                                  {orderLoadingId === r.id ? (
                                    <span className="spinner-border spinner-border-sm" role="status" />
                                  ) : (
                                    <i className="ti ti-file-certificate" />
                                  )}
                                </button>
                              )}
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleDownload(r)}
                              disabled={pdfLoadingId === r.id}
                              title="Download acknowledgment PDF"
                            >
                              {pdfLoadingId === r.id ? (
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                />
                              ) : (
                                <i className="ti ti-download" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {pdfUrl && (
          <>
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              style={{ background: "rgba(0,0,0,0.5)" }}
              onClick={closePdf}
            >
              <div
                className="modal-dialog modal-xl modal-dialog-centered"
                role="document"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content" style={{ borderRadius: 12 }}>
                  <div className="modal-header">
                    <h6 className="modal-title mb-0">{pdfTitle}</h6>
                    <div className="d-flex align-items-center gap-2">
                      <a
                        className="btn btn-sm btn-outline-primary"
                        href={pdfUrl}
                        download={`${pdfTitle || "acknowledgment"}.pdf`}
                      >
                        <i className="ti ti-download me-1" />
                        Download
                      </a>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={closePdf}
                      />
                    </div>
                  </div>
                  <div className="modal-body p-0">
                    <iframe
                      title="Acknowledgment PDF"
                      src={pdfUrl}
                      style={{ width: "100%", height: "75vh", border: "none" }}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EseprationOverview;
